import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus, BannerLayout } from 'ui-components/components/banner-message/banner-enums';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import type { LtlQuote } from '@shipstation/client-common/types/ltl';
import { useFreightQuote } from '../../freight-quote-context';
import { QuoteCard } from '../booking-wizard/components/quote-card/quote-card';
import { QuoteDetailsModal } from '../booking-wizard/components/quote-details-modal/quote-details-modal';

import style from './quotes-step.module.less';

const quotesStepMessages = defineMessages({
  insureShipment: {
    id: 'RC8i3r',
    description: 'Insure this shipment checkbox label in quotes step',
    defaultMessage: 'Insure this shipment',
  },
  sortByLabel: {
    id: 'DVTA/b',
    description: 'Sort by label in quotes step',
    defaultMessage: 'Sort by:',
  },
  sortCheapest: {
    id: '/+hZmR',
    description: 'Sort by cheapest pill in quotes step',
    defaultMessage: 'Cheapest',
  },
  sortFastest: {
    id: 'DigX7D',
    description: 'Sort by fastest pill in quotes step',
    defaultMessage: 'Fastest',
  },
  continueButton: {
    id: 'Ml4abg',
    description: 'Continue button in quotes step',
    defaultMessage: 'Continue',
  },
  loadingMessage: {
    id: 'SbLOZq',
    description: 'Loading quotes message in quotes step',
    defaultMessage: 'Loading quotes...',
  },
  emptyState: {
    id: 'bX9J7P',
    description: 'Empty state message when no quotes available in quotes step',
    defaultMessage: 'No quotes available. Adjust your shipment details and try again.',
  },
  quoteError: {
    id: 'Mxcw1d',
    description: 'Error state message in quotes step',
    defaultMessage: 'Failed to retrieve quotes.',
  },
  quotesExpired: {
    id: 'W4BTRR',
    description: 'Expired quotes banner in quotes step for a re-opened saved quote',
    defaultMessage: 'Quotes have expired. Please re-submit your shipment details to get new quotes.',
  },
  carrierCount: {
    id: 'Kze0Nv',
    description: 'Carrier count summary in quotes step header',
    defaultMessage: '{count, plural, one {# carrier} other {# carriers}}, {total, plural, one {# quote} other {# quotes}}',
  },
  collapsedSummaryWithQuote: {
    id: 'Dfkw8C',
    description: 'Collapsed quotes step summary when a quote is selected',
    defaultMessage: '{carrier} — est. delivery {date} — {price}',
  },
});

type QuotesStepProps = {
  onContinue: () => void;
};

export const QuotesStepSummary = () => {
  const { state } = useFreightQuote();
  const intl = useIntl();
  const quote = state.selectedQuote;

  if (!quote) return null;

  const formattedPrice = intl.formatNumber(quote.totalCharges, { style: 'currency', currency: 'USD' });

  return (
    <Text
      textType={TextType.Caption}
      value={intl.formatMessage(quotesStepMessages.collapsedSummaryWithQuote, {
        carrier: quote.carrierName,
        date: quote.estimatedDeliveryDate,
        price: formattedPrice,
      })}
    />
  );
};

export const QuotesStep = ({ onContinue }: QuotesStepProps) => {
  const intl = useIntl();
  const { state, localDispatch } = useFreightQuote();
  const quotes = state.quotes;
  const selectedQuote = state.selectedQuote;
  const isLoadingQuotes = state.isLoadingQuotes;
  const hasQuoteError = state.hasQuoteError;
  const quoteErrorMessage = state.quoteErrorMessage;
  const savedQuoteFreightShipmentId = state.savedQuoteFreightShipmentId;

  const [sortBy, setSortBy] = useState<'cheapest' | 'fastest'>('cheapest');
  const [isInsured, setIsInsured] = useState(false);
  const [detailsQuote, setDetailsQuote] = useState<LtlQuote | null>(null);

  const isSavedQuoteReopen = Boolean(savedQuoteFreightShipmentId);
  const hasQuotes = quotes?.length > 0;
  const isQuotesExpired = isSavedQuoteReopen && !isLoadingQuotes && !hasQuotes;
  const isEmptyState = !isLoadingQuotes && !hasQuoteError && !hasQuotes && !isQuotesExpired;

  const quotesWithCarrierCount = useMemo(() => {
    if (!quotes) return { uniqueCarrierCount: 0, totalCount: 0 };
    const uniqueCarriers = new Set(quotes.map((q) => q.carrierCode));
    return { uniqueCarrierCount: uniqueCarriers.size, totalCount: quotes.length };
  }, [quotes]);

  const sortedQuotes = useMemo(() => {
    if (!quotes) return [];
    return [...quotes].sort((a, b) => {
      if (sortBy === 'cheapest') return a.totalCharges - b.totalCharges;
      const transitDiff = a.transitDays - b.transitDays;
      if (transitDiff !== 0) return transitDiff;
      return a.totalCharges - b.totalCharges;
    });
  }, [quotes, sortBy]);

  const handleSelectQuote = useCallback(
    (quote: LtlQuote) => {
      localDispatch({ type: 'SELECT_QUOTE', quote });
    },
    [localDispatch],
  );

  const handleCloseDetails = useCallback(() => {
    setDetailsQuote(null);
  }, []);

  const handleSortCheapest = useCallback(() => setSortBy('cheapest'), []);
  const handleSortFastest = useCallback(() => setSortBy('fastest'), []);

  return (
    <FlexColumn gap={Gap.Space12}>
      <Checkbox
        checked={isInsured}
        label={intl.formatMessage(quotesStepMessages.insureShipment)}
        onChange={(checked: boolean) => setIsInsured(checked)}
      />

      {hasQuotes && (
        <FlexRow className={style['sort-controls']} gap={Gap.Space8}>
          <Text
            className={style['sort-label']}
            textType={TextType.Caption}
            value={<FormattedMessage {...quotesStepMessages.sortByLabel} />}
          />
          <Button
            className={classnames(style['sort-pill'], { [style['sort-pill-active']]: sortBy === 'cheapest' })}
            onClick={handleSortCheapest}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FormattedMessage {...quotesStepMessages.sortCheapest} />
          </Button>
          <Button
            className={classnames(style['sort-pill'], { [style['sort-pill-active']]: sortBy === 'fastest' })}
            onClick={handleSortFastest}
            theme={THEME_CONSTANTS.unstyled}
          >
            <FormattedMessage {...quotesStepMessages.sortFastest} />
          </Button>
          <Text
            formattedMessageValues={{
              count: quotesWithCarrierCount.uniqueCarrierCount,
              total: quotesWithCarrierCount.totalCount,
            }}
            textType={TextType.Caption}
            value={quotesStepMessages.carrierCount}
          />
        </FlexRow>
      )}

      {isLoadingQuotes && (
        <FlexColumn className={style['loading-state']} gap={Gap.Space16}>
          <LoadingIndicator />
          <Text textType={TextType.Caption} value={<FormattedMessage {...quotesStepMessages.loadingMessage} />} />
        </FlexColumn>
      )}

      {hasQuoteError && (
        <BannerMessageV2
          content={<Text textType={TextType.Paragraph} value={quoteErrorMessage || quotesStepMessages.quoteError} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Error}
        />
      )}

      {isQuotesExpired && (
        <BannerMessageV2
          content={<Text textType={TextType.Paragraph} value={<FormattedMessage {...quotesStepMessages.quotesExpired} />} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Warning}
        />
      )}

      {isEmptyState && (
        <FlexColumn className={style['empty-state']}>
          <Text textType={TextType.Paragraph} value={<FormattedMessage {...quotesStepMessages.emptyState} />} />
        </FlexColumn>
      )}

      {hasQuotes && (
        <FlexColumn gap={Gap.Space8}>
          {sortedQuotes.map((quote: LtlQuote) => (
            <QuoteCard
              isSelected={selectedQuote?.offerId === quote.offerId}
              key={quote.offerId}
              onSelect={handleSelectQuote}
              onViewDetails={setDetailsQuote}
              quote={quote}
            />
          ))}
        </FlexColumn>
      )}

      <div className={style['step-actions']}>
        <Button disabled={!selectedQuote} onClick={onContinue} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...quotesStepMessages.continueButton} />
        </Button>
      </div>

      {detailsQuote && <QuoteDetailsModal onClose={handleCloseDetails} quote={detailsQuote} />}
    </FlexColumn>
  );
};
